import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Stack, HStack, Button, useToast } from "@chakra-ui/react";
import {
  getLibrariesData,
  updateLibrariesData,
} from "../../../services/Libraries";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import TypeaheadWidget from "../../../CommonComponents/TypeaheadWidget";
import { getResearchCategoriesAPI } from "../../../services/Films";
import FormLoader from "../../../CommonComponents/FormLoader";
import { useSelector } from "react-redux";
import {
  handleApiError,
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "../../../util/errorHandler";
import {
  RESEARCH_CATEGORY_SLOTS,
  getChangedResearchCategorySlots,
  syncResearchCategorySlots,
  toResearchCategoryValueMap,
} from "../../../util/researchCategorySync";

// Renders nothing; keeps the field value in formData (ui:widget "hidden" does not apply to array fields)
const HiddenField = () => null;

const ResearchCategories = () => {
  const { alias } = useParams();
  const [quickSave, setQuickSave] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const previewEnabled = useSelector((state) => state.preview.isEnabled);
  const toast = useToast();
  const type = window.location.pathname.split("/")[1];
  // Record as loaded from the API, used to detect which research category slots the admin changed
  const originalDataRef = useRef({});
  // Research categories list (each entry has both `alias` and `value`), fetched once
  const researchCategoriesRef = useRef(null);

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = [
      "_id",
      "research_categories",
      "research_categories_alias",
      "research_category1",
      "research_category2",
      "research_category3",
      "research_category4",
      "research_category1_alias",
      "research_category2_alias",
      "research_category3_alias",
      "research_category4_alias",
    ];
    const rightFields = [
      "essential_reading_for_rc1",
      "essential_reading_for_rc2",
      "essential_reading_for_rc3",
      "essential_reading_for_rc4",
      "suggested_reading_for_rc1",
      "suggested_reading_for_rc2",
      "suggested_reading_for_rc3",
    ];

    const leftData = {};
    const rightData = {};

    leftFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      }
    });

    rightFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      }
    });

    return { leftData, rightData };
  };

  // Combine data from two forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  // Form change handlers
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  // Left column schema
  const leftColumnSchema = {
    title: "",
    type: "object",
    properties: {
      _id: { type: "string" },
      research_categories: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Research categories",
      },
      research_categories_alias: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Research categories alias",
      },
      research_category1: {
        type: "string",
        title: "Research category 1",
      },
      research_category2: {
        type: "string",
        title: "Research category 2",
      },
      research_category3: {
        type: "string",
        title: "Research category 3",
      },
      research_category4: {
        type: "string",
        title: "Research category 4",
      },
      research_category1_alias: {
        type: "string",
        title: "Research category 1 alias",
      },
      research_category2_alias: {
        type: "string",
        title: "Research category 2 alias",
      },
      research_category3_alias: {
        type: "string",
        title: "Research category 3 alias",
      },
      research_category4_alias: {
        type: "string",
        title: "Research category 4 alias",
      },
    },
  };

  // Right column schema
  const rightColumnSchema = {
    title: "",
    type: "object",
    properties: {
      essential_reading_for_rc1: {
        type: "string",
        title: "Essential reading for research category 1",
      },
      essential_reading_for_rc2: {
        type: "string",
        title: "Essential reading for research category 2",
      },
      essential_reading_for_rc3: {
        type: "string",
        title: "Essential reading for research category 3",
      },
      essential_reading_for_rc4: {
        type: "string",
        title: "Essential reading for research category 4",
      },
      suggested_reading_for_rc1: {
        type: "string",
        title: "Suggested reading for research category 1",
      },
      suggested_reading_for_rc2: {
        type: "string",
        title: "Suggested reading for research category 2",
      },
      suggested_reading_for_rc3: {
        type: "string",
        title: "Suggested reading for research category 3",
      },
    },
  };

  // Combined schema for preview mode
  const combinedSchema = {
    title: "",
    description: "",
    type: "object",
    properties: {
      _id: { type: "string" },
      research_categories: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Research categories",
      },
      research_categories_alias: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Research categories alias",
      },
      research_category1: {
        type: "string",
        title: "Research category 1",
      },
      research_category2: {
        type: "string",
        title: "Research category 2",
      },
      research_category3: {
        type: "string",
        title: "Research category 3",
      },
      research_category4: {
        type: "string",
        title: "Research category 4",
      },
      research_category1_alias: {
        type: "string",
        title: "Research category 1 alias",
      },
      research_category2_alias: {
        type: "string",
        title: "Research category 2 alias",
      },
      research_category3_alias: {
        type: "string",
        title: "Research category 3 alias",
      },
      research_category4_alias: {
        type: "string",
        title: "Research category 4 alias",
      },
      essential_reading_for_rc1: {
        type: "string",
        title: "Essential reading for research category 1",
      },
      essential_reading_for_rc2: {
        type: "string",
        title: "Essential reading for research category 2",
      },
      essential_reading_for_rc3: {
        type: "string",
        title: "Essential reading for research category 3",
      },
      essential_reading_for_rc4: {
        type: "string",
        title: "Essential reading for research category 4",
      },
      suggested_reading_for_rc1: {
        type: "string",
        title: "Suggested reading for research category 1",
      },
      suggested_reading_for_rc2: {
        type: "string",
        title: "Suggested reading for research category 2",
      },
      suggested_reading_for_rc3: {
        type: "string",
        title: "Suggested reading for research category 3",
      },
    },
  };

  // Left column UI schema
  const leftColumnUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    _id: { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    research_category1_alias: {
      "ui:widget": "typeahead",
    },
    research_category2_alias: {
      "ui:widget": "typeahead",
    },
    research_category3_alias: {
      "ui:widget": "typeahead",
    },
    research_category4_alias: {
      "ui:widget": "typeahead",
    },
    research_category1: { "ui:widget": "hidden" },
    research_category2: { "ui:widget": "hidden" },
    research_category3: { "ui:widget": "hidden" },
    research_category4: { "ui:widget": "hidden" },
    // Derived from research_category1_alias..research_category4_alias on save
    research_categories_alias: { "ui:field": HiddenField },
    research_categories: { "ui:field": HiddenField },
  };

  // Right column UI schema
  const rightColumnUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    "ui:style": {
      fontSize: "14px",
    },
    essential_reading_for_rc1: { "ui:emptyValue": "" },
    essential_reading_for_rc2: { "ui:emptyValue": "" },
    essential_reading_for_rc3: { "ui:emptyValue": "" },
    essential_reading_for_rc4: { "ui:emptyValue": "" },
    suggested_reading_for_rc1: { "ui:emptyValue": "" },
    suggested_reading_for_rc2: { "ui:emptyValue": "" },
    suggested_reading_for_rc3: { "ui:emptyValue": "" },
  };

  // Combined UI schema for preview mode
  const combinedUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    _id: { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    research_category1_alias: {
      "ui:widget": "typeahead",
    },
    research_category2_alias: {
      "ui:widget": "typeahead",
    },
    research_category3_alias: {
      "ui:widget": "typeahead",
    },
    research_category4_alias: {
      "ui:widget": "typeahead",
    },
    research_category1: { "ui:widget": "hidden" },
    research_category2: { "ui:widget": "hidden" },
    research_category3: { "ui:widget": "hidden" },
    research_category4: { "ui:widget": "hidden" },
    // Derived from research_category1_alias..research_category4_alias on save
    research_categories_alias: { "ui:field": HiddenField },
    essential_reading_for_rc1: { "ui:emptyValue": "" },
    essential_reading_for_rc2: { "ui:emptyValue": "" },
    essential_reading_for_rc3: { "ui:emptyValue": "" },
    essential_reading_for_rc4: { "ui:emptyValue": "" },
    suggested_reading_for_rc1: { "ui:emptyValue": "" },
    suggested_reading_for_rc2: { "ui:emptyValue": "" },
    suggested_reading_for_rc3: { "ui:emptyValue": "" },
    research_categories: { "ui:field": HiddenField },
  };

  const widgets = {
    typeahead: (props) => (
      <TypeaheadWidget
        {...props}
        fetchOptions={(inputValue) => fetchCategories(inputValue, "alias")}
      />
    ),
    typeahead1: (props) => (
      <TypeaheadWidget
        {...props}
        fetchOptions={(inputValue) => fetchCategories(inputValue, "value")}
      />
    ),
  };

  const transformOptions = (data, keyForLabelValue = "alias") => {
    return data?.map((itemArray) => {
      // Convert the array of key-value pairs into a single object
      const entity = itemArray.reduce((acc, { key, value }) => {
        acc[key] = value;
        return acc;
      }, {});

      // Use the `alias` field for label and value
      return {
        label: entity[keyForLabelValue],
        value: entity[keyForLabelValue],
      };
    });
  };

  const getResearchCategories = async () => {
    if (!researchCategoriesRef.current) {
      researchCategoriesRef.current = (await getResearchCategoriesAPI()) || [];
    }
    return researchCategoriesRef.current;
  };

  const fetchCategories = async (inputValue, keyForLabelValue = "alias") => {
    const result = await getResearchCategories();
    const transformedOptions = transformOptions(result, keyForLabelValue);

    // This function return the options
    return transformedOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };

  // Derive research_categoryN from the changed alias slots and rebuild research_categories /
  // research_categories_alias (see util/researchCategorySync). Returns null if a selected alias has
  // no matching research category.
  const syncResearchCategories = async (formData) => {
    const original = originalDataRef.current || {};
    if (getChangedResearchCategorySlots(formData, original).length === 0) {
      return formData;
    }
    const { data, missingAlias } = syncResearchCategorySlots(
      formData,
      original,
      toResearchCategoryValueMap(await getResearchCategories()),
      { aliasArrayField: "research_categories_alias" },
    );
    if (missingAlias) {
      showErrorToast(
        toast,
        `Research category "${missingAlias}" was not found. Please select it again from the list.`,
      );
      return null;
    }
    return data;
  };

  // Some records only have research_categories_alias filled and empty research_categoryN_alias slots.
  // Show those aliases in the empty slots (in order, skipping ones already in a slot). The original
  // record is left untouched, so on save these slots count as changed and syncResearchCategories
  // fills research_categoryN and rebuilds the arrays.
  const fillEmptySlotsFromArray = (data = {}) => {
    const next = { ...data };
    const inSlots = RESEARCH_CATEGORY_SLOTS.map(
      (i) => next[`research_category${i}_alias`],
    ).filter(Boolean);
    const pending = (
      Array.isArray(next.research_categories_alias)
        ? next.research_categories_alias
        : []
    )
      .map((a) => (a || "").trim())
      .filter(
        (a, index, arr) =>
          a && !inSlots.includes(a) && arr.indexOf(a) === index,
      );

    let filledAny = false;
    RESEARCH_CATEGORY_SLOTS.forEach((i) => {
      if (!next[`research_category${i}_alias`] && pending.length > 0) {
        next[`research_category${i}_alias`] = pending.shift();
        filledAny = true;
      }
    });

    if (pending.length > 0) {
      showWarningToast(
        toast,
        `Only 4 research categories are supported. These will be removed ${filledAny ? "on save" : "if a research category is changed"}: ${pending.join(", ")}`,
      );
    }
    return next;
  };

  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getLibrariesData(
            alias,
            "research-categories",
            type,
          );
          if (result) {
            originalDataRef.current = result?.data?.[0] || {};
            const data = fillEmptySlotsFromArray(result?.data?.[0]);
            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(data);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(data);
          }
        } else {
          // For new records, initialize empty forms
          const emptyData = {};
          originalDataRef.current = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch data",
        });
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      let finalFormData;

      if (previewEnabled) {
        finalFormData = combinedFormData;
      } else {
        finalFormData = combineFormData(leftFormData, rightFormData);
      }

      finalFormData = await syncResearchCategories(finalFormData);
      if (!finalFormData) {
        return;
      }

      // Remove _id from create operations
      if (!alias && finalFormData._id) {
        delete finalFormData._id;
      }

      if (alias) {
        const result = await updateLibrariesData(
          alias,
          finalFormData,
          "",
          "research-categories",
        );

        if (result?.status === 200) {
          setQuickSave((prev) => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to update data",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Box
                flex={1}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                p={4}
                borderRadius="md"
              >
                <Box fontSize="lg" fontWeight="bold" mb={4}>
                  Research Categories Fields
                </Box>
                <Form
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  widgets={widgets}
                  fontsize={"14px"}
                />
              </Box>
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Stack
                flexDirection="row"
                gap={4}
                alignItems="flex-start"
                flex={1}
              >
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Box fontSize="lg" fontWeight="bold" mb={4}>
                      Research Categories Fields
                    </Box>
                    <Form
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Form
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {/* Fixed Save Button */}
      <Box
        position="fixed"
        bottom={0}
        left={"250px"}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
      >
        <Box maxW="auto" mx="auto" display="flex" justifyContent="center">
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            width="auto"
            margin="0 auto"
            textAlign="center"
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ResearchCategories;
